import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useState } from 'react';

/**
 * ResendInviteDialog - Confirmation dialog for resending invite
 * 
 * Features:
 * - Clear confirmation message
 * - Shows recipient email
 * - Loading state during API call
 * - Error handling
 */
const ResendInviteDialog = ({ open, onClose, invite, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleResend = async () => {
    setLoading(true);
    setError(null);

    try {
      await onConfirm(invite._id);
      onClose();
    } catch (err) {
      setError(err.message || 'Davet yeniden gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  if (!invite) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Daveti Yeniden Gönder
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Typography variant="body1">
            <strong>{invite.email}</strong> adresine davet yeniden gönderilecek.
          </Typography>

          <Alert severity="info" sx={{ mt: 1 }}>
            Yeni bir davet e-postası gönderilecek ve son kullanma tarihi güncellenecektir.
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          color="inherit"
        >
          İptal
        </Button>
        <Button
          onClick={handleResend}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
        >
          {loading ? 'Gönderiliyor...' : 'Yeniden Gönder'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ResendInviteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  invite: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired
  }),
  onConfirm: PropTypes.func.isRequired
};

export default ResendInviteDialog;
